import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import './style.scss';

// react-big-calendar stacks an absolutely positioned `.rbc-events-container` over the whole day
// column, so the empty `.rbc-time-slot` divs underneath never receive hover events of their own.
// Instead of poking holes in that layer (which drag and drop relies on) we read the whole stack
// under the cursor and pick the slot out of it.
export const SLOT_TIME_ATTRIBUTE = 'data-slot-time';
// The same instant in raw milliseconds, so the drag preview can turn the hovered slot back into
// an appointment date without re-parsing the display string.
export const SLOT_VALUE_ATTRIBUTE = 'data-slot-value';

export const slotTimePropGetter = (value) => ({
  [SLOT_TIME_ATTRIBUTE]: moment(value).format('h:mm A'),
  [SLOT_VALUE_ATTRIBUTE]: value.getTime(),
});

const OFFSET_X = 14;
const OFFSET_Y = 18;
const ESTIMATED_WIDTH = 80;

const SlotTimeTooltip = ({ children, className }) => {
  const [tooltip, setTooltip] = useState(undefined);

  const frame = useRef(undefined);
  const point = useRef(undefined);
  // An HTML5 drag stops firing mousemove, so without this the chip would hang frozen over the
  // grid for the whole drag. ScheduleDragPreview owns the time readout from here on.
  const dragging = useRef(false);

  const update = useCallback(() => {
    frame.current = undefined;

    const cursor = point.current;
    if (!cursor) return;

    let slot = undefined;

    for (const element of document.elementsFromPoint(cursor.x, cursor.y)) {
      // An appointment, or a range the user is actively dragging out (which labels itself), wins
      // over the empty slot underneath it.
      if (
        element.classList.contains('rbc-event') ||
        element.classList.contains('rbc-slot-selection')
      ) {
        setTooltip(undefined);
        return;
      }
      if (element.hasAttribute(SLOT_TIME_ATTRIBUTE)) {
        slot = element;
        break;
      }
    }

    if (!slot) {
      setTooltip(undefined);
      return;
    }

    const time = slot.getAttribute(SLOT_TIME_ATTRIBUTE);
    const flip = cursor.x + OFFSET_X + ESTIMATED_WIDTH > window.innerWidth;

    setTooltip((current) =>
      current && current.time === time && current.x === cursor.x && current.y === cursor.y
        ? current
        : { time, x: cursor.x, y: cursor.y, flip },
    );
  }, []);

  const onMouseMove = useCallback(
    (event) => {
      if (dragging.current) return;
      point.current = { x: event.clientX, y: event.clientY };
      if (frame.current === undefined) frame.current = requestAnimationFrame(update);
    },
    [update],
  );

  const onMouseLeave = useCallback(() => {
    point.current = undefined;
    setTooltip(undefined);
  }, []);

  useEffect(() => {
    function onDragStart() {
      dragging.current = true;
      point.current = undefined;
      setTooltip(undefined);
    }

    function onDragEnd() {
      dragging.current = false;
    }

    window.addEventListener('dragstart', onDragStart);
    window.addEventListener('dragend', onDragEnd);
    window.addEventListener('drop', onDragEnd);

    return () => {
      window.removeEventListener('dragstart', onDragStart);
      window.removeEventListener('dragend', onDragEnd);
      window.removeEventListener('drop', onDragEnd);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    };
  }, []);

  return (
    <div className={className} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave}>
      {children}
      {!!tooltip &&
        createPortal(
          <div
            className="slot-time-tooltip"
            style={{
              left: tooltip.flip ? undefined : tooltip.x + OFFSET_X,
              right: tooltip.flip ? window.innerWidth - tooltip.x + OFFSET_X : undefined,
              top: tooltip.y + OFFSET_Y,
            }}
          >
            {tooltip.time}
          </div>,
          document.body,
        )}
    </div>
  );
};

SlotTimeTooltip.propTypes = {
  className: PropTypes.string,
};

export default SlotTimeTooltip;
